"use client";

import { Button } from "@repo/design-system/components/ui/button";
import { Calendar } from "@repo/design-system/components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@repo/design-system/components/ui/popover";
import { cn } from "@repo/design-system/lib/utils";
import { format } from "date-fns";
import { CalendarIcon } from "lucide-react";
import * as React from "react";
import { DateRange, Matcher, SelectSingleEventHandler } from "react-day-picker";

// --------------------------------------------------------------------------------
// 1) Single-Date DatePicker
// --------------------------------------------------------------------------------
interface SingleDatePickerProps extends React.HTMLAttributes<HTMLDivElement> {
  selectedDate: Date | undefined;
  setSelectedDate: (date: Date | undefined) => void;
  disabled?: boolean;
  calendarProps?: React.ComponentProps<typeof Calendar>;
}

export function DatePicker({
  className,
  selectedDate,
  setSelectedDate,
  disabled,
  calendarProps,
}: SingleDatePickerProps) {
  // track the popover's open state
  const [open, setOpen] = React.useState(false);

  return (
    <div className={cn("grid gap-2", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant="outline"
            className={cn(
              "w-auto min-w-[150px] justify-start text-left font-normal",
              !selectedDate && "text-muted-foreground"
            )}
            disabled={disabled}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {selectedDate ? (
              format(selectedDate, "PPP")
            ) : (
              <span>Pick a date</span>
            )}
          </Button>
        </PopoverTrigger>

        <PopoverContent
          className="w-auto p-0 z-[100]"
          align="start"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <Calendar
            {...calendarProps}
            mode="single"
            selected={selectedDate}
            onSelect={setSelectedDate as any}
            // `disabled` on Calendar is react-day-picker's disabled-days
            // matcher, not "disable this control" — so it has to come from
            // calendarProps. It used to be overwritten by the component's own
            // boolean `disabled` immediately after the spread, which silently
            // dropped every `calendarProps={{ disabled: ... }}` a caller
            // passed. That boolean already disables the trigger, and a popover
            // that cannot open has no days to disable.
            disabled={calendarProps?.disabled}
            defaultMonth={selectedDate}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}

// --------------------------------------------------------------------------------
// 2) Range DatePicker
// --------------------------------------------------------------------------------
export type { DateRange } from "react-day-picker";

interface DatePickerWithRangeProps
  extends React.HTMLAttributes<HTMLDivElement> {
  selectedDate: DateRange | undefined;
  setSelectedDate: React.Dispatch<React.SetStateAction<DateRange | undefined>>;
  // Add header prop similar to footer
  header?: (closePopover: () => void) => React.ReactNode;
  footer?: (closePopover: () => void) => React.ReactNode;
  isAdmin?: boolean;
  allowPastDates?: boolean;
  minDate?: Date;
  maxDate?: Date;
  disabledDates?: { before?: Date; after?: Date; from?: Date; to?: Date };
}

export function DatePickerWithRange({
  className,
  selectedDate,
  setSelectedDate,
  header,
  footer,
  isAdmin = false,
  allowPastDates = false,
  minDate,
  maxDate,
  disabledDates,
}: DatePickerWithRangeProps) {
  // track popover open state
  const [open, setOpen] = React.useState(false);

  // expose a small function to close the popover
  const closePopover = React.useCallback(() => {
    setOpen(false);
  }, []);

  // Get today's date at midnight
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // Build disabled dates configuration
  const buildDisabledDates = () => {
    const disabled: { before?: Date; after?: Date; from?: Date; to?: Date } = {};
    
    // If custom disabled dates are provided, use those
    if (disabledDates) {
      return disabledDates;
    }
    
    // Default behavior: respect allowPastDates and isAdmin
    if (!isAdmin && !allowPastDates) {
      disabled.before = today;
    }
    
    // Apply min/max date constraints if provided
    if (minDate) {
      disabled.before = minDate;
    }
    if (maxDate) {
      disabled.after = maxDate;
    }
    
    // Filter out undefined values to match the expected type
    const filteredDisabled: { before?: Date; after?: Date; from?: Date; to?: Date } = {};
    if (disabled.before) filteredDisabled.before = disabled.before;
    if (disabled.after) filteredDisabled.after = disabled.after;
    if (disabled.from) filteredDisabled.from = disabled.from;
    if (disabled.to) filteredDisabled.to = disabled.to;
    
    return filteredDisabled;
  };

  const finalDisabledDates = buildDisabledDates();

  return (
    <div className={cn("grid gap-2", className)}>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant="outline"
            className={cn(
              "w-auto min-w-[150px] justify-start text-left font-normal",
              !selectedDate && "text-muted-foreground"
            )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {selectedDate?.from ? (
              selectedDate.to ? (
                <>
                  {format(selectedDate.from, "LLL dd, y")} –{" "}
                  {format(selectedDate.to, "LLL dd, y")}
                </>
              ) : (
                format(selectedDate.from, "LLL dd, y")
              )
            ) : (
              <span>Pick a date</span>
            )}
          </Button>
        </PopoverTrigger>

        <PopoverContent
          className="w-auto p-0 z-[100]"
          align="start"
          onOpenAutoFocus={(e) => e.preventDefault()}
        >
          <div className="p-3">
            {header?.(closePopover)}
            <Calendar
              mode="range"
              defaultMonth={selectedDate?.from}
              selected={selectedDate}
              onSelect={(range) => {
                if (!isAdmin && !allowPastDates) {
                  if (range?.from && range.from < today) {
                    return;
                  }
                  if (range?.to && range.to < today) {
                    return;
                  }
                }
                // Create a new object to ensure React detects the state change
                // react-day-picker may reuse/mutate the same object reference
                setSelectedDate(range ? { from: range.from, to: range.to } : undefined);
              }}
              numberOfMonths={2}
              disabled={Object.keys(finalDisabledDates).length > 0 ? finalDisabledDates as Matcher : undefined}
            />
            {footer?.(closePopover)}
          </div>
        </PopoverContent>
      </Popover>
    </div>
  );
}
